import {
	AtlBrowserStorageLocal,
	AtlBrowserStorageSession,
} from '@atlassian/browser-storage-controls';
import { fg } from '@atlassian/jira-feature-gating';
import { STOREJS_KEY_PREFIX } from './constants.tsx';
import {
	type ExpirableWebStorageProvider,
	type ExpirableWebStorageProviderNew,
	type SetItemResult,
	WebStorageType,
} from './types.tsx';

import { isStorageAPIAvailable } from './isStorageAPIAvailable.tsx';

export const createExpirableStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
): ExpirableWebStorageProvider | ExpirableWebStorageProviderNew => {
	if (!appPrefix) {
		throw Error("appPrefix can not be empty. It's used to separate apps");
	}
	const namespacedStore = store.namespace(appPrefix);
	// Uses the same key format as store.js: '__storejs_' + namespace + '_' + key
	const getKeyWithPrefix = (key: string) => `${STOREJS_KEY_PREFIX}${appPrefix}_${key}`;
	// Uses the same expiration key format as store.js expirePlugin:
	// Sub-store namespace = __storejs_{appPrefix}_expire_mixin
	// Sub-store prefix = __storejs_ + sub-store namespace + _ = __storejs___storejs_{appPrefix}_expire_mixin_
	const getExpireKeyWithPrefix = (key: string) =>
		`${STOREJS_KEY_PREFIX}${STOREJS_KEY_PREFIX}${appPrefix}_expire_mixin_${key}`;
	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;
	const browserStorage =
		storageType === WebStorageType.LocalStorage
			? globalThis.localStorage
			: globalThis.sessionStorage;

	const remove = (key: string): void => {
		try {
			if (fg('jfp-vulcan-browser-storage-migration')) {
				if (!isStorageAPIAvailable()) {
					// eslint-disable-next-line no-console
					console.warn(`${storageType} is unavailable (e.g. SSR). Failed to remove item ${key}`);
					return;
				}
				storageAPI.removeItem(getKeyWithPrefix(key));
				storageAPI.removeItem(getExpireKeyWithPrefix(key));
			}
			// Remove from old store as well, or the only store if FG is off
			namespacedStore.remove(key);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(`Error removing item with key=${key}:`, error);
		}
	};

	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any, expiration?: number): void => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to set item ${key}`);
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					const expireKey = getExpireKeyWithPrefix(key);
					storageAPI.setItem(prefixedKey, JSON.stringify(value)).then(
						(setItemResult) => {
							if (setItemResult.status !== 'SUCCESS') {
								storageAPI.removeItem(expireKey);
								return;
							}
							// Matches store.js: set(key, val) without expiration clears any existing expiration
							if (expiration !== undefined) {
								storageAPI.setStrictlyNecessaryItem(expireKey, JSON.stringify(expiration));
							} else {
								storageAPI.removeItem(expireKey);
							}
						},
						(error) => {
							// eslint-disable-next-line no-console
							console.error(`Error setting item with key=${key}:`, error);
						},
					);
				} else {
					namespacedStore.set(key, value, expiration);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, error);
			}
		},

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		setAsync: async (key: string, value: any, expiration?: number): Promise<void> => {
			try {
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to set item ${key}`);
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					const setItemResult: SetItemResult = await storageAPI.setItem(
						prefixedKey,
						JSON.stringify(value),
					);
					if (setItemResult !== undefined) {
						if (setItemResult.status === 'BLOCKED') {
							storageAPI.removeItem(prefixedKey);
							throw new Error('write was blocked by storage or consent preferences.');
						}
						if (setItemResult.status !== 'SUCCESS') {
							throw new Error('write failed');
						}
					}
					const expireKey = getExpireKeyWithPrefix(key);
					if (expiration !== undefined) {
						storageAPI.setStrictlyNecessaryItem(expireKey, JSON.stringify(expiration));
					} else {
						storageAPI.removeItem(expireKey);
					}
				} else {
					namespacedStore.set(key, value, expiration);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, error);
				throw error;
			}
		},

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any, expiration?: number): void => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(
							`${storageType} is unavailable (e.g. SSR). Failed to set strictly necessary item ${key}`,
						);
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.setStrictlyNecessaryItem(prefixedKey, JSON.stringify(value));
					// Matches store.js: set(key, val) without expiration clears any existing expiration
					const expireKey = getExpireKeyWithPrefix(key);
					if (expiration !== undefined) {
						storageAPI.setStrictlyNecessaryItem(expireKey, JSON.stringify(expiration));
					} else {
						storageAPI.removeItem(expireKey);
					}
				} else {
					// this has to be done when setStrictlyNecessaryItem comes into usage, and it should have a fallback when FG is turned off
					namespacedStore.set(key, value, expiration);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, error);
			}
		},

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to get item ${key}`);
						return undefined;
					}
					// Matches store.js expirePlugin: check expiration before reading value
					const expireKey = getExpireKeyWithPrefix(key);
					const expirationStr = storageAPI.getItem(expireKey);
					if (expirationStr) {
						const expiration = JSON.parse(expirationStr);
						if (expiration < Date.now()) {
							// Expired: remove both value and expiration keys
							storageAPI.removeItem(getKeyWithPrefix(key));
							storageAPI.removeItem(expireKey);
							return;
						}
					}
					const prefixedKey = getKeyWithPrefix(key);
					const itemStr = storageAPI.getItem(prefixedKey);
					if (typeof itemStr === 'string') {
						return JSON.parse(itemStr);
					}
				} else {
					return namespacedStore.get(key);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error getting item with key=${key}:`, error);
			}
		},
		getExpiration: (key: string): number | undefined => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(
							`${storageType} is unavailable (e.g. SSR). Failed to get expiration for item ${key}`,
						);
						return undefined;
					}
					// Matches store.js expirePlugin: expirations.get(key) — returns raw timestamp
					const expireKey = getExpireKeyWithPrefix(key);
					const expirationStr = storageAPI.getItem(expireKey);
					if (typeof expirationStr === 'string') {
						return JSON.parse(expirationStr);
					}
					return undefined;
				}
				return namespacedStore.getExpiration(key);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(`Error getting expiration for item with key=${key}:`, error);
				return undefined;
			}
		},

		remove,

		removeExpiredKeys: (): void => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to remove expired keys`);
						return;
					}
					const expirePrefix = `${STOREJS_KEY_PREFIX}${STOREJS_KEY_PREFIX}`;
					const expireSuffix = '_expire_mixin_';
					for (let i = storageAPI.length() - 1; i >= 0; i--) {
						const storageKey = storageAPI.key(i);
						const suffixIndex = storageKey ? storageKey.indexOf(expireSuffix) : -1;
						if (storageKey && storageKey.startsWith(expirePrefix) && suffixIndex !== -1) {
							const expirationStr = storageAPI.getItem(storageKey);
							if (expirationStr) {
								const expiration = JSON.parse(expirationStr);
								if (expiration < Date.now()) {
									// Derive the value key: __storejs_ + namespace + _ + originalKey
									const namespace = storageKey.substring(expirePrefix.length, suffixIndex);
									const originalKey = storageKey.substring(suffixIndex + expireSuffix.length);
									const valueKey = `${STOREJS_KEY_PREFIX}${namespace}_${originalKey}`;
									storageAPI.removeItem(valueKey);
									storageAPI.removeItem(storageKey);
								}
							}
						}
					}
					namespacedStore.removeExpiredKeys(); // clean up old keys too
				} else {
					namespacedStore.removeExpiredKeys();
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error removing expired keys:', error);
			}
		},
		// Clears the complete storage
		clearAll: () => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to clear storage`);
						return;
					}
					storageAPI.clear();
					// clean up old keys too
					namespacedStore.clearAll();
				} else {
					namespacedStore.clearAll();
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error clearing storage:', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					// eslint-disable-next-line no-console
					console.warn(`${storageType} is unavailable (e.g. SSR). Failed to get storage length`);
					return 0;
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					return storageAPI.length();
				}
				return browserStorage?.length || 0;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting storage length:', err);
				return 0;
			}
		},
		key: (index: number): string | null => {
			try {
				if (!isStorageAPIAvailable()) {
					// eslint-disable-next-line no-console
					console.warn(
						`${storageType} is unavailable (e.g. SSR). Failed to get key at index ${index}`,
					);
					return null;
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					return storageAPI.key(index);
				}
				return browserStorage?.key(index) ?? null;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting key at index:', err);
				return null;
			}
		},
	};
};
